import Script from 'next/script';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

import { Styles, css } from '@flipster/styled-system/css';
import { alert } from '@flipster/ui/Alert';
import GoogleIcon from '@flipster/ui/assets/svgs/social/ic-google.svg';
import { isServer } from '@flipster/utils/env-helper';
import segment, { ReferralEvent } from '@flipster/utils/segment';
import { getReferralInfo } from '@flipster/utils/sessionStorage';

import { authButtonCSSRaw } from '@web/app/[locale]/(web)/_auth/base-styles';
import { useSocialLogin } from '@web/hooks/useSocialLogin';

interface Props {
  label?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  css?: Styles;
}

export const ContinueWithGoogle = ({ label, onClick, disabled, css: cssProps }: Props) => {
  const googleRef = useRef<HTMLDivElement | null>(null);

  const [load, setLoad] = useState<boolean>(false);

  const loginHandler = useSocialLogin();

  const isInAppBrowser = checkInAppBrowser();

  const onClickContinueWithGoogle = () => {
    onClick?.();

    if (!isInAppBrowser) return;
    alert.show({
      title: 'Error',
      message:
        'Please access flipster.io on a separate\nbrowser such as Google Chrome, to\naccess social sign-in or sign-up.',
      confirmText: 'Dismiss',
    });
  };

  useLayoutEffect(() => {
    if (!load || isInAppBrowser) return;
    window.google?.accounts.id.initialize({
      client_id: '189506763495-ctvjm07l1ge9gem6s7egl0hb85pgauvk.apps.googleusercontent.com',
      callback: (res: { credential: string }) => loginHandler('GOOGLE', res.credential),
    });
    if (!googleRef.current) return;
    window.google?.accounts.id.renderButton(googleRef.current, {
      size: 'large',
      width: googleRef.current.offsetWidth + 'px',
      click_listener() {
        const referralInfo = getReferralInfo();
        segment.track(ReferralEvent.SignInSsoClick, {
          auth_type: 'google',
          ...referralInfo,
        });
      },
    });
  }, [load, isInAppBrowser, loginHandler]);

  return (
    <>
      <Script id='google-auth-script' src='https://accounts.google.com/gsi/client' onReady={() => setLoad(true)} />
      <button
        type='button'
        onClick={onClickContinueWithGoogle}
        disabled={(!load && !isInAppBrowser) || disabled}
        className={css(authButtonCSSRaw, cssProps)}
      >
        <GoogleIcon width={20} height={20} />
        {label}
        <div
          className={css({
            pos: 'absolute',
            top: 0,
            left: 0,
            w: '100%',
            minW: 0,
            transform: 'scaleY(1.5)',
            opacity: 0.0001,
          })}
          ref={googleRef}
        />
      </button>
    </>
  );
};

const checkInAppBrowser = () => {
  if (isServer) return false;
  const agent = navigator.userAgent.toLowerCase();
  // 카카오, 인스타그램, 페이스북, 틱톡
  return /kakao|instagram|fb|trill/i.test(agent);
};
